interface CacheEntry<T> {
    data: T;
    timestamp: number;
}

const GROUPS_CACHE_KEY = "groups_cache_v1";
const GROUPS_CACHE_DIRTY_KEY = "groups_cache_v1_dirty";
const GROUPS_ETAG_KEY = "groups_etag_v1";

type RawGroup = Record<string, unknown>;

let memoryCache: RawGroup[] | null = null;

function parseCachedGroups(raw: string | null): RawGroup[] | null {
    if (!raw) {
        return null;
    }

    try {
        const entry: CacheEntry<RawGroup[]> = JSON.parse(raw);
        if (!Array.isArray(entry.data)) {
            return null;
        }

        return entry.data;
    } catch {
        return null;
    }
}

function readCache(): RawGroup[] | null {
    if (memoryCache) {
        return memoryCache;
    }

    try {
        const cachedGroups = parseCachedGroups(
            localStorage.getItem(GROUPS_CACHE_KEY),
        );
        memoryCache = cachedGroups;
        return cachedGroups;
    } catch {
        return null;
    }
}

function writeCache(groups: RawGroup[]): void {
    const entry: CacheEntry<RawGroup[]> = {
        data: groups,
        timestamp: Date.now(),
    };

    memoryCache = groups;

    try {
        localStorage.setItem(GROUPS_CACHE_KEY, JSON.stringify(entry));
    } catch {
        // best effort
    }
}

function isDirty(): boolean {
    try {
        return localStorage.getItem(GROUPS_CACHE_DIRTY_KEY) === "1";
    } catch {
        return false;
    }
}

function markDirty(): void {
    try {
        localStorage.setItem(GROUPS_CACHE_DIRTY_KEY, "1");
    } catch {
        // best effort
    }
}

function markSynced(): void {
    try {
        localStorage.setItem(GROUPS_CACHE_DIRTY_KEY, "0");
    } catch {
        // best effort
    }
}

function getEtag(): string | null {
    try {
        return localStorage.getItem(GROUPS_ETAG_KEY);
    } catch {
        return null;
    }
}

function setEtag(etag: string | null): void {
    try {
        if (etag) {
            localStorage.setItem(GROUPS_ETAG_KEY, etag);
        } else {
            localStorage.removeItem(GROUPS_ETAG_KEY);
        }
    } catch {
        // best effort
    }
}

function clear(): void {
    memoryCache = null;

    try {
        localStorage.removeItem(GROUPS_CACHE_KEY);
        localStorage.removeItem(GROUPS_CACHE_DIRTY_KEY);
        localStorage.removeItem(GROUPS_ETAG_KEY);
    } catch {
        // best effort
    }
}

export default {
    get: readCache,
    set: writeCache,
    isDirty,
    markDirty,
    markSynced,
    getEtag,
    setEtag,
    clear,
};
